import React, { useState, useEffect } from 'react';
import { Role, RoleFormData } from '../types';
import {
  HiUsers,
  HiPlus,
  HiPencil,
  HiTrash,
  HiX,
  HiCheckCircle,
  HiXCircle,
  HiShieldCheck,
  HiHome,
  HiOfficeBuilding,
  HiCube,
  HiArchive,
  HiDocumentText,
  HiPrinter,
  HiLocationMarker,
  HiCalculator,
  HiReceiptTax,
  HiCog,
  HiUser,
  HiCurrencyDollar,
  HiClipboardList,
  HiShoppingBag,
  HiCollection,
  HiSwitchHorizontal,
  HiStar,
  HiColorSwatch,
} from 'react-icons/hi';
import apiService from '../services/api';
import { useNotification } from '../hooks/useNotification';
import Notification from './Notification';
import ConfirmDialog from './ConfirmDialog';
import { normalizeRole, normalizePaginaList } from '../constants/pageRoutes';
import './Settings.css';

type SettingsTab = 'permissoes' | 'roles' | 'tarifas';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('permissoes');
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState<RoleFormData>({
    name: '',
    description: '',
    permissionIds: []
  });
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [permissaosId, setPermissaosId] = useState<number | null>(null);
  const [permissoes, setPermissoes] = useState<any[]>([]);
  const [loadingPermissoes, setLoadingPermissoes] = useState(false);
  const [showPermissaoModal, setShowPermissaoModal] = useState(false);
  const [editingPermissao, setEditingPermissao] = useState<any | null>(null);
  const [permissaoForm, setPermissaoForm] = useState({
    permissao: '',
    nome: '',
    descricao: ''
  });
  const [permissaoToDelete, setPermissaoToDelete] = useState<number | null>(null);
  const [savingPermissao, setSavingPermissao] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<number | null>(null);

  // Estados para Tarifas
  const [tarifas, setTarifas] = useState<any[]>([]);
  const [loadingTarifas, setLoadingTarifas] = useState(false);
  const [showTarifaModal, setShowTarifaModal] = useState(false);
  const [editingTarifa, setEditingTarifa] = useState<any | null>(null);
  const [tarifaForm, setTarifaForm] = useState({
    nome: '',
    origem: '',
    destino: '',
    valor: 0,
    kg_max: 0,
    acrescimento_embalagem: 0,
    ativo: true,
  });
  const [tarifaFormDisplay, setTarifaFormDisplay] = useState({
    valor: '0,00',
    kg_max: '0,00',
    acrescimento_embalagem: '0,00'
  });
  const [tarifaToDelete, setTarifaToDelete] = useState<number | null>(null);
  const [savingTarifa, setSavingTarifa] = useState(false);
  const [togglingTarifaId, setTogglingTarifaId] = useState<number | null>(null);

  const { notifications, removeNotification, showSuccess, showWarning } = useNotification() as {
    notifications: Array<{ id: number; message: string; type: string; duration: number }>;
    removeNotification: (id: number) => void;
    showSuccess: (message: string, duration?: number) => number;
    showWarning: (message: string, duration?: number) => number;
  };

  useEffect(() => {
    loadRoles();
    loadPermissoes();
    if (activeTab === 'tarifas') {
      loadTarifas();
    }
  }, [activeTab]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getRoles();
      // A API retorna { data: [...] } ou diretamente o array
      const rolesList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      // Garantir paginaList e campos de exibição normalizados
      const normalizedRoles = rolesList.map((role: any) => {
        const normalized = normalizeRole(role) || role;
        return {
          ...normalized,
          name: role.nome || role.name,
          description: role.descricao || role.description,
          permissions: Array.isArray(role.permissions) ? role.permissions : [],
          permissionIds: Array.isArray(role.permissionIds) ? role.permissionIds : [],
          users: Array.isArray(role.users) ? role.users : [],
          paginaList: normalized.paginaList || [],
        };
      });
      setRoles(normalizedRoles);
    } catch (error: any) {
      console.error('Erro ao carregar roles:', error);
      const errorMessage = error.message || 'Erro ao carregar roles. Verifique se o endpoint está disponível no servidor.';
      setError(errorMessage);
      // Se o erro for 500, pode ser que o endpoint não exista ainda no backend
      // Nesse caso, apenas inicializamos com array vazio
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
  };

  const handleOpenRoleModal = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      const roleData = role as any;
      // Extrair paginaList se disponível (array, JSON string, snake_case)
      const pages = normalizePaginaList(
        roleData.paginaList ?? roleData.pagina_list ?? roleData.paginas
      );
      setSelectedPages(pages);
      setPermissaosId(roleData.permissaos_id || null);
      setRoleForm({
        name: role.name || roleData.nome || '',
        description: role.description || roleData.descricao || '',
        permissionIds: []
      });
    } else {
      setEditingRole(null);
      setSelectedPages([]);
      setPermissaosId(null);
      setRoleForm({
        name: '',
        description: '',
        permissionIds: []
      });
    }
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setEditingRole(null);
    setSelectedPages([]);
    setPermissaosId(null);
    setRoleForm({
      name: '',
      description: '',
      permissionIds: []
    });
  };

  const handlePageToggle = (pageId: string) => {
    setSelectedPages(prev => {
      if (prev.includes(pageId)) {
        return prev.filter(id => id !== pageId);
      } else {
        return [...prev, pageId];
      }
    });
  };

  const handleSaveRole = async () => {
    if (!roleForm.name || !roleForm.name.trim()) {
      showWarning('Por favor, preencha o nome do role');
      return;
    }

    if (!permissaosId) {
      showWarning('Por favor, selecione uma permissão base');
      return;
    }

    try {
      if (editingRole) {
        await apiService.updateRole(editingRole.id, {
          nome: roleForm.name,
          descricao: roleForm.description || undefined,
          permissaos_id: permissaosId,
          paginaList: selectedPages,
        });
        showSuccess('Role atualizado com sucesso!');
    } else {
        await apiService.createRole({
          nome: roleForm.name,
          descricao: roleForm.description || undefined,
          permissaos_id: permissaosId,
          paginaList: selectedPages,
        });
        showSuccess('Role criado com sucesso!');
    }
    handleCloseRoleModal();
      loadRoles();
    } catch (error: any) {
      console.error('Erro ao salvar role:', error);
      showWarning(error.message || 'Erro ao salvar role');
    }
  };

  const handleDeleteRole = (roleId: number) => {
    setRoleToDelete(roleId);
    setShowConfirmDialog(true);
  };

  const confirmDeleteRole = async () => {
    if (roleToDelete === null) return;

    try {
      await apiService.deleteRole(roleToDelete);
      showSuccess('Role excluído com sucesso!');
      setShowConfirmDialog(false);
      setRoleToDelete(null);
      loadRoles();
    } catch (error: any) {
      console.error('Erro ao excluir role:', error);
      showWarning(error.message || 'Erro ao excluir role');
      setShowConfirmDialog(false);
      setRoleToDelete(null);
    }
  };

  // Lista de todas as páginas do sistema
  const systemPages = [
    { id: 'dashboard', name: 'Dashboard', icon: HiHome, path: '/dashboard', color: '#3b82f6' },
    { id: 'clients', name: 'Clientes', icon: HiUsers, path: '/clients', color: '#10b981' },
    { id: 'solicitacoes-exclusao', name: 'Exclusão de contas', icon: HiTrash, path: '/solicitacoes-exclusao', color: '#dc2626' },
    { id: 'catalogo', name: 'Catálogo', icon: HiColorSwatch, path: '/catalogo', color: '#db2777' },
    { id: 'rewards', name: 'SendingTo Rewards', icon: HiStar, path: '/rewards', color: '#e85d04' },
    { id: 'suppliers', name: 'Fornecedores', icon: HiOfficeBuilding, path: '/suppliers', color: '#f59e0b' },
    { id: 'boxes', name: 'Caixas / Malas', icon: HiCube, path: '/boxes', color: '#8b5cf6' },
    { id: 'organizar-caixa', name: 'Organizar Caixa', icon: HiArchive, path: '/organizar-caixa', color: '#0f766e' },
    { id: 'invoices', name: 'Invoices', icon: HiDocumentText, path: '/invoices', color: '#ef4444' },
    { id: 'payments', name: 'Cobranças PIX', icon: HiCurrencyDollar, path: '/payments', color: '#0d9488' },
    { id: 'pagamentos-retirada', name: 'Pagamentos', icon: HiClipboardList, path: '/pagamentos-retirada', color: '#ff6b35' },
    { id: 'currency', name: 'Conversor', icon: HiSwitchHorizontal, path: '/currency', color: '#0f766e' },
    { id: 'labels', name: 'Etiquetas', icon: HiPrinter, path: '/labels', color: '#06b6d4' },
    { id: 'coletas', name: 'Coletas', icon: HiCollection, path: '/coletas', color: '#0ea5e9' },
    { id: 'tracking', name: 'Rastreamento', icon: HiLocationMarker, path: '/tracking', color: '#ec4899' },
    { id: 'rastreamento', name: 'Retirada', icon: HiShoppingBag, path: '/rastreamento', color: '#17A2B8' },
    { id: 'costs', name: 'Gestão de Custos', icon: HiCalculator, path: '/costs', color: '#14b8a6' },
    { id: 'central-fiscal', name: 'Central Fiscal', icon: HiReceiptTax, path: '/central-fiscal', color: '#6366f1' },
    { id: 'users', name: 'Usuários', icon: HiUser, path: '/users', color: '#f97316' },
    { id: 'settings', name: 'Configurações', icon: HiCog, path: '/settings', color: '#64748b' },
    { id: 'profile', name: 'Perfil', icon: HiUser, path: '/profile', color: '#84cc16' },
  ];


  const loadPermissoes = async () => {
    try {
      setLoadingPermissoes(true);
      const data = await apiService.getPermissaos();
      const permissoesList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setPermissoes(permissoesList);
    } catch (error: any) {
      console.error('Erro ao carregar permissões:', error);
      showWarning(error.message || 'Erro ao carregar permissões');
      setPermissoes([]);
    } finally {
      setLoadingPermissoes(false);
    }
  };

  const handleOpenPermissaoModal = (permissao?: any) => {
    if (permissao) {
      setEditingPermissao(permissao);
      setPermissaoForm({
        permissao: permissao.permissao || '',
        nome: permissao.nome || '',
        descricao: permissao.descricao || ''
      });
    } else {
      setEditingPermissao(null);
      setPermissaoForm({
        permissao: '',
        nome: '',
        descricao: ''
      });
    }
    setShowPermissaoModal(true);
  };

  const handleClosePermissaoModal = () => {
    setShowPermissaoModal(false);
    setEditingPermissao(null);
    setPermissaoForm({
      permissao: '',
      nome: '',
      descricao: ''
    });
    setSavingPermissao(false);
  };

  const handleSavePermissao = async () => {
    if (!permissaoForm.permissao.trim() || !permissaoForm.nome.trim()) {
      showWarning('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setSavingPermissao(true);
    try {
      if (editingPermissao) {
        await apiService.updatePermissao(editingPermissao.id, {
          permissao: permissaoForm.permissao,
          nome: permissaoForm.nome,
          descricao: permissaoForm.descricao || undefined
        });
        showSuccess('Permissão atualizada com sucesso!');
      } else {
        await apiService.createPermissao({
          permissao: permissaoForm.permissao,
          nome: permissaoForm.nome,
          descricao: permissaoForm.descricao || undefined
        });
        showSuccess('Permissão criada com sucesso!');
      }
      handleClosePermissaoModal();
      loadPermissoes();
    } catch (error: any) {
      console.error('Erro ao salvar permissão:', error);
      showWarning(error.message || 'Erro ao salvar permissão');
    } finally {
      setSavingPermissao(false);
    }
  };

  const handleDeletePermissao = async () => {
    if (permissaoToDelete === null) return;

    try {
      await apiService.deletePermissao(permissaoToDelete);
      showSuccess('Permissão excluída com sucesso!');
      setPermissaoToDelete(null);
      loadPermissoes();
    } catch (error: any) {
      console.error('Erro ao excluir permissão:', error);
      showWarning(error.message || 'Erro ao excluir permissão');
    }
  };

  // ========== TARIFAS ==========
  // Funções de formatação
  const formatCurrency = (value: number): string => {
    if (!value && value !== 0) return '0,00';
    // Formata com pontos de milhar e vírgula decimal
    const parts = value.toFixed(2).split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Adiciona pontos de milhar
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `${formattedInteger},${decimalPart}`;
  };

  const formatWeight = (value: number): string => {
    if (!value && value !== 0) return '0,00';
    return value.toFixed(2).replace('.', ',');
  };

  const parseCurrency = (value: string): number => {
    if (!value) return 0;
    // Remove pontos (milhares) e substitui vírgula por ponto
    const cleaned = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const parseWeight = (value: string): number => {
    if (!value) return 0;
    // Remove tudo exceto números e vírgula
    const cleaned = value.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto
    const normalized = cleaned.replace(',', '.');
    return parseFloat(normalized) || 0;
  };

  const loadTarifas = async () => {
    try {
      setLoadingTarifas(true);
      const data = await apiService.getTarifas({ per_page: 200 });
      const tarifasList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setTarifas(tarifasList);
    } catch (error: any) {
      console.error('Erro ao carregar tarifas:', error);
      showWarning(error.message || 'Erro ao carregar tarifas');
      setTarifas([]);
    } finally {
      setLoadingTarifas(false);
    }
  };

  const isTarifaAtiva = (tarifa: any) =>
    tarifa?.ativo !== false && tarifa?.ativo !== 0 && tarifa?.ativo !== '0';

  const handleToggleTarifaAtivo = async (tarifa: any) => {
    if (!tarifa?.id || togglingTarifaId != null) return;
    setTogglingTarifaId(tarifa.id);
    try {
      const response = await apiService.toggleTarifaAtivo(tarifa.id);
      const updated = response?.data || response;
      const nowActive = isTarifaAtiva(updated);
      showSuccess(nowActive ? 'Tarifa ativada com sucesso!' : 'Tarifa desativada com sucesso!');
      setTarifas((prev) =>
        prev.map((t) => (t.id === tarifa.id ? { ...t, ...updated, ativo: nowActive } : t))
      );
    } catch (error: any) {
      console.error('Erro ao alterar status da tarifa:', error);
      showWarning(error.message || 'Erro ao alterar status da tarifa');
    } finally {
      setTogglingTarifaId(null);
    }
  };

  const handleOpenTarifaModal = (tarifa?: any) => {
    if (tarifa) {
      setEditingTarifa(tarifa);
      const valorNum = Number(tarifa.valor) || 0;
      const kgMaxNum = Number(tarifa.kg_max) || 0;
      const acrescimentoNum = Number(tarifa.acrescimento_embalagem) || 0;
      setTarifaForm({
        nome: tarifa.nome || '',
        origem: tarifa.origem || '',
        destino: tarifa.destino || '',
        valor: valorNum,
        kg_max: kgMaxNum,
        acrescimento_embalagem: acrescimentoNum,
        ativo: isTarifaAtiva(tarifa),
      });
      setTarifaFormDisplay({
        valor: formatCurrency(valorNum),
        kg_max: formatWeight(kgMaxNum),
        acrescimento_embalagem: formatCurrency(acrescimentoNum)
      });
    } else {
      setEditingTarifa(null);
      setTarifaForm({
        nome: '',
        origem: '',
        destino: '',
        valor: 0,
        kg_max: 0,
        acrescimento_embalagem: 0,
        ativo: true,
      });
      setTarifaFormDisplay({
        valor: '0,00',
        kg_max: '0,00',
        acrescimento_embalagem: '0,00'
      });
    }
    setShowTarifaModal(true);
  };

  const handleCloseTarifaModal = () => {
    setShowTarifaModal(false);
    setEditingTarifa(null);
    setTarifaForm({
      nome: '',
      origem: '',
      destino: '',
      valor: 0,
      kg_max: 0,
      acrescimento_embalagem: 0,
      ativo: true,
    });
    setTarifaFormDisplay({
      valor: '0,00',
      kg_max: '0,00',
      acrescimento_embalagem: '0,00'
    });
    setSavingTarifa(false);
  };

  const handleSaveTarifa = async () => {
    if (!tarifaForm.nome.trim() || !tarifaForm.origem.trim() || !tarifaForm.destino.trim()) {
      showWarning('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    // Converter valores formatados para número
    const valorNum = parseCurrency(tarifaFormDisplay.valor);
    const kgMaxNum = parseWeight(tarifaFormDisplay.kg_max);
    const acrescimentoNum = parseCurrency(tarifaFormDisplay.acrescimento_embalagem);

    if (valorNum <= 0) {
      showWarning('O valor deve ser maior que zero');
      return;
    }

    if (kgMaxNum <= 0) {
      showWarning('O peso máximo deve ser maior que zero');
      return;
    }

    setSavingTarifa(true);
    try {
      const dataToSave = {
        nome: tarifaForm.nome,
        origem: tarifaForm.origem,
        destino: tarifaForm.destino,
        valor: valorNum,
        kg_max: kgMaxNum,
        acrescimento_embalagem: acrescimentoNum,
        ativo: !!tarifaForm.ativo,
      };

      if (editingTarifa) {
        await apiService.updateTarifa(editingTarifa.id, dataToSave);
        showSuccess('Tarifa atualizada com sucesso!');
      } else {
        await apiService.createTarifa(dataToSave);
        showSuccess('Tarifa criada com sucesso!');
      }
      handleCloseTarifaModal();
      loadTarifas();
    } catch (error: any) {
      console.error('Erro ao salvar tarifa:', error);
      showWarning(error.message || 'Erro ao salvar tarifa');
    } finally {
      setSavingTarifa(false);
    }
  };

  const handleDeleteTarifa = async () => {
    if (tarifaToDelete === null) return;

    try {
      await apiService.deleteTarifa(tarifaToDelete);
      showSuccess('Tarifa excluída com sucesso!');
      setTarifaToDelete(null);
      loadTarifas();
    } catch (error: any) {
      console.error('Erro ao excluir tarifa:', error);
      showWarning(error.message || 'Erro ao excluir tarifa');
    }
  };

  const renderTarifas = () => (
    <div className="settings-content">
      <div className="roles-header">
        <div>
          <h2>Gestão de Tarifas</h2>
          <p className="roles-subtitle">Gerencie as tarifas de transporte</p>
        </div>
        <button className="btn-primary" onClick={() => handleOpenTarifaModal()}>
          {React.createElement(HiPlus as any, { size: 18 })}
          <span>Nova Tarifa</span>
        </button>
      </div>

      {loadingTarifas && (
        <div className="loading-message" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '2rem' }}>
          <div className="loading-spinner-large"></div>
          <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Carregando tarifas...</p>
        </div>
      )}

      {!loadingTarifas && tarifas.length === 0 && (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          <p>Nenhuma tarifa encontrada.</p>
        </div>
      )}

      {!loadingTarifas && tarifas.length > 0 && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nome</th>
                <th>Origem</th>
                <th>Destino</th>
                <th>Valor (R$)</th>
                <th>Peso Máx (kg)</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {tarifas.map((tarifa, index) => (
                <tr key={tarifa.id} style={isTarifaAtiva(tarifa) ? undefined : { opacity: 0.72 }}>
                  <td>{index + 1}</td>
                  <td><strong>{tarifa.nome}</strong></td>
                  <td>{tarifa.origem}</td>
                  <td>{tarifa.destino}</td>
                  <td>R$ {formatCurrency(Number(tarifa.valor))}</td>
                  <td>{formatWeight(Number(tarifa.kg_max))} kg</td>
                  <td>
                    <span
                      className={`badge ${isTarifaAtiva(tarifa) ? 'badge-success' : 'badge-danger'}`}
                    >
                      {isTarifaAtiva(tarifa) ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleOpenTarifaModal(tarifa)}
                        title="Editar tarifa"
                      >
                        {React.createElement(HiPencil as any, { size: 18 })}
                      </button>
                      {isTarifaAtiva(tarifa) ? (
                        <button
                          className="btn-icon btn-warning"
                          onClick={() => handleToggleTarifaAtivo(tarifa)}
                          disabled={togglingTarifaId === tarifa.id}
                          title="Desativar tarifa"
                        >
                          {React.createElement(HiXCircle as any, { size: 18 })}
                        </button>
                      ) : (
                        <button
                          className="btn-icon btn-success"
                          onClick={() => handleToggleTarifaAtivo(tarifa)}
                          disabled={togglingTarifaId === tarifa.id}
                          title="Ativar tarifa"
                        >
                          {React.createElement(HiCheckCircle as any, { size: 18 })}
                        </button>
                      )}
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => setTarifaToDelete(tarifa.id)}
                        title="Excluir tarifa"
                      >
                        {React.createElement(HiTrash as any, { size: 18 })}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Tarifa */}
      {showTarifaModal && (
        <div className="modal-overlay" onClick={handleCloseTarifaModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-wrapper">
                {React.createElement(HiCurrencyDollar as any, { size: 24 })}
                <h3>{editingTarifa ? 'Editar Tarifa' : 'Nova Tarifa'}</h3>
              </div>
              <button className="modal-close" onClick={handleCloseTarifaModal}>
                {React.createElement(HiX as any, { size: 24 })}
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  value={tarifaForm.nome}
                  onChange={(e) => setTarifaForm(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: São Paulo - Rio"
                  required
                />
              </div>
              <div className="form-group">
                <label>Origem *</label>
                <input
                  type="text"
                  value={tarifaForm.origem}
                  onChange={(e) => setTarifaForm(prev => ({ ...prev, origem: e.target.value }))}
                  placeholder="Ex: São Paulo"
                  required
                />
              </div>
              <div className="form-group">
                <label>Destino *</label>
                <input
                  type="text"
                  value={tarifaForm.destino}
                  onChange={(e) => setTarifaForm(prev => ({ ...prev, destino: e.target.value }))}
                  placeholder="Ex: Rio de Janeiro"
                  required
                />
              </div>
              <div className="form-group">
                <label>Valor (R$) *</label>
                <input
                  type="text"
                  value={tarifaFormDisplay.valor}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Permite apenas números, vírgula e ponto
                    const cleaned = value.replace(/[^\d,.]/g, '');
                    // Garante apenas uma vírgula e pontos apenas como separadores de milhar
                    const parts = cleaned.split(',');
                    let integerPart = parts[0] || '';
                    // Remove pontos e depois adiciona novamente como separadores de milhar
                    integerPart = integerPart.replace(/\./g, '');
                    if (integerPart) {
                      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    }
                    let formatted = integerPart;
                    if (parts.length > 1) {
                      formatted = `${formatted},${parts.slice(1).join('').substring(0, 2)}`;
                    }
                    setTarifaFormDisplay(prev => ({ ...prev, valor: formatted }));
                  }}
                  onBlur={(e) => {
                    const numValue = parseCurrency(e.target.value);
                    setTarifaFormDisplay(prev => ({ ...prev, valor: formatCurrency(numValue) }));
                    setTarifaForm(prev => ({ ...prev, valor: numValue }));
                  }}
                  placeholder="2.800,34"
                  required
                />
              </div>
              <div className="form-group">
                <label>Peso Máximo (kg) *</label>
                <input
                  type="text"
                  value={tarifaFormDisplay.kg_max}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Permite apenas números e vírgula
                    const cleaned = value.replace(/[^\d,]/g, '');
                    // Garante apenas uma vírgula
                    const parts = cleaned.split(',');
                    let formatted = parts[0] || '';
                    if (parts.length > 1) {
                      formatted = `${formatted},${parts.slice(1).join('').substring(0, 2)}`;
                    }
                    setTarifaFormDisplay(prev => ({ ...prev, kg_max: formatted }));
                  }}
                  onBlur={(e) => {
                    const numValue = parseWeight(e.target.value);
                    setTarifaFormDisplay(prev => ({ ...prev, kg_max: formatWeight(numValue) }));
                    setTarifaForm(prev => ({ ...prev, kg_max: numValue }));
                  }}
                  placeholder="1,80"
                  required
                />
              </div>
        <div className="form-group">
                <label>Acréscimo Embalagem (R$)</label>
                <input
                  type="text"
                  value={tarifaFormDisplay.acrescimento_embalagem}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Permite apenas números, vírgula e ponto
                    const cleaned = value.replace(/[^\d,.]/g, '');
                    // Garante apenas uma vírgula e pontos apenas como separadores de milhar
                    const parts = cleaned.split(',');
                    let integerPart = parts[0] || '';
                    // Remove pontos e depois adiciona novamente como separadores de milhar
                    integerPart = integerPart.replace(/\./g, '');
                    if (integerPart) {
                      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    }
                    let formatted = integerPart;
                    if (parts.length > 1) {
                      formatted = `${formatted},${parts.slice(1).join('').substring(0, 2)}`;
                    }
                    setTarifaFormDisplay(prev => ({ ...prev, acrescimento_embalagem: formatted }));
                  }}
                  onBlur={(e) => {
                    const numValue = parseCurrency(e.target.value);
                    setTarifaFormDisplay(prev => ({ ...prev, acrescimento_embalagem: formatCurrency(numValue) }));
                    setTarifaForm(prev => ({ ...prev, acrescimento_embalagem: numValue }));
                  }}
                  placeholder="0,00"
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!tarifaForm.ativo}
                    onChange={(e) => setTarifaForm(prev => ({ ...prev, ativo: e.target.checked }))}
                  />
                  <span>Tarifa ativa</span>
                </label>
                <small style={{ color: '#6b7280', display: 'block', marginTop: '0.35rem' }}>
                  Tarifas inativas não aparecem na seleção de Caixas e Coletas.
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseTarifaModal} disabled={savingTarifa}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveTarifa}
                disabled={savingTarifa}
              >
                {savingTarifa ? 'Salvando...' : `${editingTarifa ? 'Atualizar' : 'Criar'} Tarifa`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {tarifaToDelete !== null && (
        <div className="modal-overlay" onClick={() => setTarifaToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Confirmar Exclusão</h3>
              <button className="modal-close" onClick={() => setTarifaToDelete(null)}>
                {React.createElement(HiX as any, { size: 24 })}
              </button>
            </div>
            <div className="modal-body">
              <p>Tem certeza que deseja excluir esta tarifa? Esta ação não pode ser desfeita.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setTarifaToDelete(null)}>
                Cancelar
              </button>
              <button className="btn-danger" onClick={handleDeleteTarifa}>
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPermissoes = () => (
    <div className="settings-content">
      <div className="roles-header">
        <div>
          <h2>Gestão de Permissões</h2>
          <p className="roles-subtitle">Gerencie as permissões do sistema</p>
        </div>
        <button className="btn-primary" onClick={() => handleOpenPermissaoModal()}>
          {React.createElement(HiPlus as any, { size: 18 })}
          <span>Nova Permissão</span>
        </button>
      </div>

      {loadingPermissoes && (
        <div className="loading-message" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '2rem' }}>
          <div className="loading-spinner-large"></div>
          <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Carregando permissões...</p>
        </div>
      )}

      {!loadingPermissoes && permissoes.length === 0 && (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          <p>Nenhuma permissão encontrada.</p>
        </div>
      )}

      {!loadingPermissoes && permissoes.length > 0 && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Permissão</th>
                <th>Nome</th>
                <th>Descrição</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {permissoes.map((permissao, index) => (
                <tr key={permissao.id}>
                  <td>{index + 1}</td>
                  <td>
                    <code style={{ background: '#f0f0f0', padding: '4px 8px', borderRadius: '4px' }}>
                      {permissao.permissao}
                    </code>
                  </td>
                  <td><strong>{permissao.nome}</strong></td>
                  <td>{permissao.descricao || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleOpenPermissaoModal(permissao)}
                        title="Editar permissão"
                      >
                        {React.createElement(HiPencil as any, { size: 18 })}
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => setPermissaoToDelete(permissao.id)}
                        title="Excluir permissão"
                      >
                        {React.createElement(HiTrash as any, { size: 18 })}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Permissão */}
      {showPermissaoModal && (
        <div className="modal-overlay" onClick={handleClosePermissaoModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-wrapper">
                {React.createElement(HiShieldCheck as any, { size: 24 })}
                <h3>{editingPermissao ? 'Editar Permissão' : 'Nova Permissão'}</h3>
              </div>
              <button className="modal-close" onClick={handleClosePermissaoModal}>
                {React.createElement(HiX as any, { size: 24 })}
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Permissão (código) *</label>
                <input
                  type="text"
                  value={permissaoForm.permissao}
                  onChange={(e) => setPermissaoForm(prev => ({ ...prev, permissao: e.target.value }))}
                  placeholder="Ex: admin, gerente, funcionario"
                  required
                />
              </div>
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  value={permissaoForm.nome}
                  onChange={(e) => setPermissaoForm(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Administrador"
                  required
                />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  rows={3}
                  value={permissaoForm.descricao}
                  onChange={(e) => setPermissaoForm(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descreva o propósito desta permissão"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleClosePermissaoModal} disabled={savingPermissao}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleSavePermissao}
                disabled={savingPermissao}
              >
                {savingPermissao ? 'Salvando...' : `${editingPermissao ? 'Atualizar' : 'Criar'} Permissão`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {permissaoToDelete !== null && (
        <div className="modal-overlay" onClick={() => setPermissaoToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Confirmar Exclusão</h3>
              <button className="modal-close" onClick={() => setPermissaoToDelete(null)}>
                {React.createElement(HiX as any, { size: 24 })}
              </button>
            </div>
            <div className="modal-body">
              <p>Tem certeza que deseja excluir esta permissão? Esta ação não pode ser desfeita.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setPermissaoToDelete(null)}>
                Cancelar
              </button>
              <button className="btn-danger" onClick={handleDeletePermissao}>
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderRoles = () => {

    return (
      <div className="settings-content">
        <div className="roles-header">
          <div>
            <h2>Gestão de Roles</h2>
            <p className="roles-subtitle">Gerencie permissões e acessos do sistema</p>
          </div>
          <button className="btn-primary" onClick={() => handleOpenRoleModal()}>
            {React.createElement(HiPlus as any, { size: 18 })}
            <span>Novo Role</span>
          </button>
        </div>

        {loading && (
          <div className="loading-message" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '2rem' }}>
            <div className="loading-spinner-large"></div>
            <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Carregando roles...</p>
          </div>
        )}

        {error && !loading && (
          <div className="error-message" style={{ padding: '20px', margin: '20px 0', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33' }}>
            <p><strong>Erro ao carregar roles:</strong> {error}</p>
            <p style={{ fontSize: '14px', marginTop: '10px', color: '#666' }}>
              O endpoint <code>/api/v1/settings/roles</code> pode não estar disponível no servidor ou está retornando um erro.
            </p>
            <button
              onClick={loadRoles}
              style={{ marginTop: '10px', padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !error && roles.length === 0 && (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <p>Nenhum role encontrado.</p>
          </div>
        )}

        <div className="roles-list">
          {!loading && !error && roles.map(role => (
            <div key={role.id} className="role-card">
              <div className="role-header">
                <div className="role-info">
                  <div className="role-icon">
                    {React.createElement(HiShieldCheck as any, { size: 24 })}
                  </div>
                  <div>
                    <h3>{role.name}</h3>
                    <p>{role.description}</p>
                  </div>
                </div>
                <div className="role-actions">
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => handleOpenRoleModal(role)}
                    title="Editar role"
                  >
                    {React.createElement(HiPencil as any, { size: 18 })}
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDeleteRole(role.id)}
                    title="Excluir role"
                  >
                    {React.createElement(HiTrash as any, { size: 18 })}
                  </button>
                </div>
              </div>
              <div className="role-permissions">
                <div className="permissions-header">
                  <h4>
                    {React.createElement(HiCheckCircle as any, { size: 18 })}
                    <span>Permissões ({(role as any).paginaList ? (role as any).paginaList.length : 0})</span>
                  </h4>
                </div>
                <div className="permissions-grid">
                  {Array.isArray((role as any).paginaList) && (role as any).paginaList.length > 0 ? (
                    (role as any).paginaList.map((pageId: string) => {
                      const page = systemPages.find(p => p.id === pageId);
                      if (!page) return null;
                      const IconComponent = page.icon;
                      return (
                        <div
                          key={pageId}
                          className="permission-tag"
                          style={{
                            background: `${page.color}15`,
                            borderColor: page.color,
                            color: page.color
                          }}
                        >
                          {React.createElement(IconComponent as any, { size: 16 })}
                          <span>{page.name}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="permission-empty" style={{ padding: '0.5rem', fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>
                      Nenhuma página selecionada
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal de Role */}
        {showRoleModal && (
          <div className="modal-overlay" onClick={handleCloseRoleModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title-wrapper">
                  {React.createElement(HiShieldCheck as any, { size: 24 })}
                  <h3>{editingRole ? 'Editar Role' : 'Novo Role'}</h3>
                </div>
                <button className="modal-close" onClick={handleCloseRoleModal}>
                  {React.createElement(HiX as any, { size: 24 })}
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome do Role</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Administrador"
                  />
                </div>
                <div className="form-group">
                  <label>Descrição</label>
                  <textarea
                    rows={2}
                    value={roleForm.description}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descreva o propósito deste role"
                  />
                </div>
                <div className="form-group">
                  <label>Permissão Base *</label>
                  <select
                    value={permissaosId || ''}
                    onChange={(e) => setPermissaosId(e.target.value ? Number(e.target.value) : null)}
                    required
                    style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-primary)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                  >
                    <option value="">Selecione uma permissão base</option>
                    {permissoes.map(perm => (
                      <option key={perm.id} value={perm.id}>
                        {perm.nome || perm.name} {perm.descricao || perm.description ? `- ${perm.descricao || perm.description}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Páginas com Acesso</label>
                  <div className="permissions-selector">
                    <div className="permission-page-group">
                      <div className="permission-page-header">
                        <span className="page-icon">📄</span>
                        <h5>Páginas do Sistema</h5>
                        <span className="page-count">({systemPages.length})</span>
                      </div>
                      <div className="permission-list">
                        {systemPages.map(page => {
                          const isChecked = selectedPages.includes(page.id);
                          const IconComponent = page.icon;

                          return (
                            <label
                              key={page.id}
                              className="permission-item"
                              style={isChecked ? {
                                borderColor: page.color,
                                background: `${page.color}08`
                              } : {}}
                            >
                            <input
                              type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handlePageToggle(page.id);
                                }}
                                className="permission-checkbox-input"
                              />
                              <span
                                className="permission-checkbox-custom"
                                style={isChecked ? {
                                  background: page.color,
                                  borderColor: page.color
                                } : {}}
                              ></span>
                              <div className="permission-info">
                                <div className="permission-page-info">
                                  <span
                                    className="page-icon-small"
                                    style={{ color: page.color }}
                                  >
                                    {React.createElement(IconComponent as any, { size: 20 })}
                                  </span>
                                  <strong className="permission-name">{page.name}</strong>
                                </div>
                                <span className="permission-description">{page.path}</span>
                            </div>
                          </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={handleCloseRoleModal}>
                  Cancelar
                </button>
                <button className="btn-primary" onClick={handleSaveRole}>
                  {editingRole ? 'Atualizar' : 'Criar'} Role
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="settings-container">
      <div className="settings-breadcrumb">
        <span>Painel</span>
        <span>/</span>
        <span>Configurações</span>
      </div>

      <h1 className="settings-title">Configurações</h1>

      <div className="settings-layout">
        <div className="settings-sidebar">
          <button
            className={`settings-tab ${activeTab === 'permissoes' ? 'active' : ''}`}
            onClick={() => handleTabChange('permissoes')}
          >
            {React.createElement(HiShieldCheck as any, { size: 20 })}
            <span>Permissões</span>
          </button>
          <button
            className={`settings-tab ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => handleTabChange('roles')}
          >
            {React.createElement(HiUsers as any, { size: 20 })}
            <span>Roles</span>
          </button>
          <button
            className={`settings-tab ${activeTab === 'tarifas' ? 'active' : ''}`}
            onClick={() => handleTabChange('tarifas')}
          >
            {React.createElement(HiCurrencyDollar as any, { size: 20 })}
            <span>Tarifas</span>
          </button>
        </div>

        <div className="settings-main">
          {activeTab === 'permissoes' && renderPermissoes()}
          {activeTab === 'roles' && renderRoles()}
          {activeTab === 'tarifas' && renderTarifas()}
        </div>
      </div>

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>

      {showConfirmDialog && (
        <ConfirmDialog
          isOpen={showConfirmDialog}
          onCancel={() => {
            setShowConfirmDialog(false);
            setRoleToDelete(null);
          }}
          onConfirm={confirmDeleteRole}
          title="Confirmar Exclusão"
          message="Tem certeza que deseja excluir este role?"
          confirmText="Excluir"
          cancelText="Cancelar"
          type="danger"
        />
      )}
    </div>
  );
};

export default Settings;
